import { Component, h } from "preact";
import EventController from "../../../shared/controller/event-controller";
import Uploader from "../../uploader";
import {
  IDictionary,
  ILabelOptionValue,
  IOption,
  IReactConfiguration,
} from "../../../typings";
import OutputController from "../../../shared/controller/output-controller";
import TemplateController from "../../../shared/controller/template-controller";
import LabelColorPickerComponent from "./LabelColorPickerComponent";
import LabelSvgSprite from "../../label-uploader/label-svg-sprite";

export default class LabelColorComponent extends Component<
  ILabelColorProps,
  ILabelColorState
> {
  private colorHashTable: IDictionary<ILabelOptionValue> = {};

  constructor(props: ILabelColorProps) {
    super(props);

    this.state = {
      colors: [],
    };

    this.removeColor = this.removeColor.bind(this);
    this.startEditColor = this.startEditColor.bind(this);
    this.openAddColorPanel = this.openAddColorPanel.bind(this);
    this.addColor = this.addColor.bind(this);
    this.showColorsFilter = this.showColorsFilter.bind(this);
    this.colorToKey = this.colorToKey.bind(this);

    this.prepareColorHashTable(props.option.values);
  }

  componentDidMount(): void {
    let colors: string[] = this.props.outputController.getValue([
      "image",
      this.props.option.code,
    ]);
    const multiple: boolean =
      this.props.outputController.getIsMultipleAttributebyCode(
        this.props.option.code,
      );
    if (colors || !multiple) {
      colors = multiple ? colors : [colors];
      this.setState({
        colors: colors
          .filter((color: string) => typeof color === "string")
          .map((value: string) => this.getColor(value)),
      });
    }

    this.loadEventListeners();
  }

  componentWillUnmount(): void {
    this.resetInput();
  }

  loadEventListeners(): void {
    const multiple: boolean =
      this.props.outputController.getIsMultipleAttributebyCode(
        this.props.option.code,
      );

    let eventName: string = EventController.getSaveEventName([
      "image",
      this.props.option.code,
    ]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const colorsRaw: string[] = this.props.outputController.getValue([
          "image",
          this.props.option.code,
        ]);

        const colors: (string[] | string)[] = multiple
          ? colorsRaw
          : [colorsRaw];

        this.setState({
          colors: colors
            .filter((color: string) => typeof color === "string")
            .map((value: string) => this.getColor(value)),
        });
      });

    eventName = EventController.getSaveEventName(["image", "original"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        if (
          this.props.option.attributes?.color_auto_detected?.value === false
        ) {
          return;
        }
        this.resetInput();
      });
  }

  private getColor(value: string): ILabelOptionValue {
    return this.props.option.values.find(
      (optionValue: ILabelOptionValue): boolean => optionValue.value === value,
    );
  }

  private startEditColor(index: number): void {
    this.setState({
      editColorIndex: index,
      newColor: false,
    });
  }

  private removeColor(index: number): void {
    let colors: string[] = this.props.outputController.getValue([
      "image",
      this.props.option.code,
    ]);
    colors = this.props.outputController.getIsMultipleAttributebyCode(
      this.props.option.code,
    )
      ? colors
      : [colors];
    const percentages: string[] = this.props.outputController.getValue([
      "image",
      this.props.option.attributes?.color_percentage?.value,
    ]);
    const factoryColors: string[] = this.props.outputController.getValue([
      "image",
      this.props.option.attributes?.factory_color?.value,
    ])
      ? this.props.outputController.getValue([
          "image",
          this.props.option.attributes?.factory_color?.value,
        ])
      : percentages?.map(() => "");

    this.props.outputController.startEventTransaction();
    this.props.outputController
      .setValue(
        ["image", this.props.option.code],
        [
          colors.filter(
            (_: string, colorIndex: number) => colorIndex !== index,
          ),
        ],
      )
      .then(() => {})
      .catch(() => {});
    this.props.outputController
      .setValue(
        ["image", this.props.option.attributes?.color_percentage?.value],
        [
          percentages
            ? percentages.filter(
                (_: string, percentageIndex: number) =>
                  percentageIndex !== index,
              )
            : [],
        ],
      )
      .then(() => {})
      .catch(() => {});
    this.props.outputController
      .setValue(
        ["image", this.props.option.attributes?.factory_color?.value],
        [
          factoryColors
            ? factoryColors.filter(
                (_: string, factoryIndex: number) => factoryIndex !== index,
              )
            : [],
        ],
      )
      .then(() => {})
      .catch(() => {});
    this.props.outputController.commitEventTransaction();
  }

  private setColor(index: number, colorValue: string): void {
    const multiple: boolean =
      this.props.outputController.getIsMultipleAttributebyCode(
        this.props.option.code,
      );
    let colors: string[] | string = this.props.outputController.getValue([
      "image",
      this.props.option.code,
    ]);
    this.setState({ editColorIndex: undefined });
    if ((multiple ? colors[index] : colors) === colorValue) {
      return;
    }
    if (multiple) {
      colors[index] = colorValue;
    } else {
      colors = colorValue;
    }
    this.props.outputController.setValue(
      ["image", this.props.option.code],
      [colors],
    );
    const colorPercentage: number[] = this.props.outputController.getValue([
      "image",
      this.props.option.attributes?.color_percentage?.value,
    ]);
    if (colorPercentage) {
      colorPercentage[index] = 0.05001;
      this.props.outputController.setValue(
        ["image", this.props.option.attributes?.color_percentage?.value],
        [colorPercentage],
      );
    }
    const factoryColors: string[] = this.props.outputController.getValue([
      "image",
      this.props.option.attributes?.factory_color?.value,
    ])
      ? this.props.outputController.getValue([
          "image",
          this.props.option.attributes?.factory_color?.value,
        ])
      : (colorPercentage
          ? colorPercentage
          : typeof colors === "string"
            ? [""]
            : colors
        ).map(() => "");
    factoryColors[index] = "";
    this.props.outputController.setValue(
      ["image", this.props.option.attributes?.factory_color?.value],
      [factoryColors],
    );
  }

  private openAddColorPanel(): void {
    this.setState({ newColor: true, editColorIndex: undefined });
  }

  private addColor(color: string): void {
    const colorsRaw: any = this.props.outputController.getValue([
      "image",
      this.props.option.code,
    ]);
    const multiple: boolean =
      this.props.outputController.getIsMultipleAttributebyCode(
        this.props.option.code,
      );
    const colorsCheck: string[] = multiple ? colorsRaw : [colorsRaw];
    if (multiple && colorsCheck.indexOf(color) !== -1) {
      this.removeColor(colorsCheck.indexOf(color));
    }
    const colors: string[] = multiple
      ? this.props.outputController.getValue(["image", this.props.option.code])
      : [
          this.props.outputController.getValue([
            "image",
            this.props.option.code,
          ]),
        ];
    const colorPercentageKey: string =
      this.props.option.attributes?.color_percentage?.value;
    const colorPercentage: number[] = this.props.outputController.getValue([
      "image",
      colorPercentageKey,
    ]);
    const factoryColorsKey: string =
      this.props.option.attributes?.factory_color?.value;
    const factoryColors: string[] = this.props.outputController.getValue([
      "image",
      factoryColorsKey,
    ])
      ? this.props.outputController.getValue(["image", factoryColorsKey])
      : colorPercentage?.map(() => "");
    const index: number = multiple ? colors.length : 0;
    colors[index] = color;
    if (colorPercentage) {
      colorPercentage[index] = 0.05001;
    }
    if (factoryColors) {
      factoryColors[index] = "";
    }
    this.props.outputController.startEventTransaction();
    this.props.outputController
      .setValue(["image", this.props.option.code], [colors])
      .then(() => {})
      .catch(() => {});
    if (colorPercentageKey) {
      this.props.outputController
        .setValue(
          ["image", colorPercentageKey],
          colorPercentage ? [colorPercentage] : [],
        )
        .then(() => {})
        .catch(() => {});
    }
    if (factoryColorsKey) {
      this.props.outputController
        .setValue(
          ["image", factoryColorsKey],
          factoryColors ? [factoryColors] : [],
        )
        .then(() => {})
        .catch(() => {});
    }
    this.props.outputController.commitEventTransaction();
    this.setState({ newColor: false });
  }

  private prepareColorHashTable(values: Array<ILabelOptionValue>): void {
    const unique: string[] = [];
    values
      .filter((value: ILabelOptionValue) => {
        if (unique.indexOf(value.value) !== -1) {
          return false;
        }
        unique.push(value.value);
        return true;
      })
      .forEach((value: ILabelOptionValue) => {
        this.colorHashTable[this.colorToKey(value)] = value;
      });
  }

  colorToKey(color: ILabelOptionValue): string {
    return (
      color.meta.name.toLowerCase() +
      "|" +
      color.attributes.hex.value.toLowerCase() +
      "|" +
      color.attributes.pantone.value.toLowerCase()
    );
  }

  private showColorsFilter(color: ILabelOptionValue, index: number): boolean {
    if (!color) {
      return false;
    }

    if (this.state.colors.length < 3) {
      return true;
    }

    if (!this.props.option.attributes?.color_percentage?.value) {
      return true;
    }

    const colors: any = this.props.outputController.getValue([
      "image",
      this.props.option.attributes?.color_percentage?.value,
    ]);

    if (colors === null || colors.length === 0) {
      return false;
    }

    return colors[index] > 0.025;
  }

  render(): JSX.Element {
    const multiple: boolean =
      this.props.outputController.getIsMultipleAttributebyCode(
        this.props.option.code,
      );

    const colors: ILabelOptionValue[] = this.state.colors.filter(
      this.showColorsFilter,
    );

    return this.state.colors.length > 0 || !multiple ? (
      <div
        className={
          this.state.editColorIndex === undefined && !this.state.newColor
            ? ""
            : "min-h-color-picker"
        }
      >
        <div
          className={
            "grid gap-8 p-6 lg:p-8" +
            (!multiple && colors.length === 0 && !this.state.newColor
              ? " hidden"
              : "")
          }
        >
          <div className="grid grid-cols-12 mb-4">
            <div className="col-span-6 sm:col-span-4 text-xl font-bold">
              {this.props.option.meta.fields.swatch}
            </div>
            <div className="col-span-4 sm:col-span-3 text-xl font-bold">
              {this.props.option.meta.fields.pantone}
            </div>
            <div className="col-span-3 text-xl font-bold hidden sm:block">
              {this.props.option.meta.fields.hex}
            </div>
            <div />
            <div />
          </div>
          {colors.map((value: ILabelOptionValue, index: number) => (
            <div
              key={index}
              className="grid items-center justify-center grid-cols-12"
            >
              {value && (
                <div className="flex items-center col-span-6 sm:col-span-4 space-x-2">
                  <div
                    className="w-8 h-8 border border-solid rounded-full border-gray"
                    style={
                      "background-color: " + value.attributes.hex.value + ";"
                    }
                  />
                  <span>{value.meta.name}</span>
                </div>
              )}
              {value && (
                <div className="col-span-4 sm:col-span-3">
                  <div>{value.attributes.pantone.value}</div>
                </div>
              )}
              {value && (
                <div className="col-span-3 hidden sm:block">
                  <div>{value.attributes.hex.value}</div>
                </div>
              )}
              <div className="col-span-1 col-start-11">
                <div
                  className={"p-4 md:py-6 lg:px-3 lg:py-2.5 cursor-pointer"}
                  onClick={() => this.startEditColor(index)}
                >
                  <LabelSvgSprite
                    viewBox="0 0 32 32"
                    cssClass={"w-6 h-6"}
                    conf={this.props.conf}
                    imageCode="edit-pencil"
                    alt={this.props.option.meta.fields.edit}
                  />
                </div>
                {this.state.editColorIndex === index && (
                  <LabelColorPickerComponent
                    {...this.props}
                    colorHashTable={this.colorHashTable}
                    colors={colors}
                    color={value}
                    setColor={(colorValue: string) =>
                      this.setColor(index, colorValue)
                    }
                    exclude={colors.map(this.colorToKey)}
                    closePanel={() =>
                      this.setState({ editColorIndex: undefined })
                    }
                  />
                )}
              </div>
              <div className="col-span-1">
                <div
                  className={
                    "p-4 md:py-6 lg:px-3 lg:py-2.5 cursor-pointer" +
                    (multiple ? "" : " hidden")
                  }
                  onClick={() => this.removeColor(index)}
                >
                  <LabelSvgSprite
                    viewBox="0 0 32 32"
                    cssClass={"w-6 h-6"}
                    conf={this.props.conf}
                    imageCode="close"
                    alt={this.props.option.meta.fields.delete}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          className={
            "flex justify-end p-6 bg-white rounded-b-lg" +
            (multiple || colors.length === 0 ? "" : " hidden")
          }
        >
          <a
            className="no-underline px-4 py-3 font-semibold rounded-md cursor-pointer select-none bg-blue-light text-blue lg:px-6"
            onClick={this.openAddColorPanel}
          >
            {this.props.option.meta.fields.add}
          </a>

          {this.state.newColor && (
            <LabelColorPickerComponent
              {...this.props}
              colorHashTable={this.colorHashTable}
              colors={this.state.colors}
              setColor={(colorValue: string) => {
                this.addColor(colorValue);
              }}
              exclude={this.state.colors
                .filter(this.showColorsFilter)
                .map(this.colorToKey)}
              closePanel={() => this.setState({ newColor: false })}
            />
          )}
        </div>
      </div>
    ) : (
      <div className="flex flex-col items-center p-6 text-center lg:p-8">
        <span className="text-2xl font-semibold text-black">
          {this.props?.option?.meta?.fields?.placeholder}
        </span>
      </div>
    );
  }

  private resetInput(): void {
    const multiple: boolean =
      this.props.outputController.getIsMultipleAttributebyCode(
        this.props.option.code,
      );

    this.props.outputController.setValue(
      ["image", this.props.option.code],
      this.props.option.default
        ? this.props.option.default
        : multiple
          ? []
          : null,
    );
  }
}

interface ILabelColorProps {
  main: Uploader;
  conf: IReactConfiguration;
  option: IOption;

  eventController: EventController;
  outputController: OutputController;
  templateController: TemplateController;
}

interface ILabelColorState {
  colors: ILabelOptionValue[];
  editColorIndex?: number;
  newColor: boolean;
}
